import { api } from "@glasshalf/api-client";
import type { ColumnDef } from "@tanstack/react-table";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowRightLeft, Globe2, PackageCheck, Plus, RefreshCw, UploadCloud } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ConfirmImpactDialog } from "../../components/ConfirmImpactDialog";
import { DataTable } from "../../components/DataTable";
import { SavedViews, type SavedView } from "../../components/SavedViews";
import { StatusBadge } from "../../components/StatusBadge";
import { formatNumber, formatSizeLabel, normalizeText } from "../../lib/format";
import { locations, productRows } from "../../lib/selectors";
import type { AnyRecord, AppData, DrawerState, ProductRow, SkuRow } from "../../lib/types";

type InventoryPageProps = {
  data: AppData;
  drawer: DrawerState;
  setDrawer: (drawer: DrawerState) => void;
};

type PendingAction = "adjust" | "transfer" | "allocate" | "pushChannelStock" | null;
type InventoryMode = "stock" | "transfer" | "allocate";
type AdjustmentDirection = "increase" | "decrease";

export function InventoryPage({ data, setDrawer }: InventoryPageProps) {
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState("");
  const [expandedProductKey, setExpandedProductKey] = useState<string | null>(null);
  const [selectedSku, setSelectedSku] = useState<SkuRow | null>(null);
  const [action, setAction] = useState<PendingAction>(null);
  const [adjustmentDirection, setAdjustmentDirection] = useState<AdjustmentDirection>("increase");
  const [quantity, setQuantity] = useState(1);
  const [allocationQuantity, setAllocationQuantity] = useState(1);
  const [reason, setReason] = useState("");
  const locationOptions = locations(data).filter((location) => location.active !== false);
  const sellableLocations = locationOptions.filter((location) => Number(location.isSellable ?? 0) === 1 || location.sellableState === "sellable");
  const defaultLocation = String(sellableLocations[0]?.code || locationOptions[0]?.code || "WH_MAIN");
  const [fromLocation, setFromLocation] = useState(defaultLocation);
  const [toLocation, setToLocation] = useState(defaultLocation);
  const channelOptions = onlineMallChannels(data);
  const [selectedChannelCode, setSelectedChannelCode] = useState(channelOptions[0]?.code || "CAFE24_KR");
  const products = productRows(data);
  const modeParam = params.get("mode");
  const activeMode: InventoryMode = modeParam === "transfer" || modeParam === "allocate" ? modeParam : "stock";
  const activeView = params.get("view") || "all";
  const setInventoryParams = (next: { mode?: InventoryMode; view?: string }) => {
    const search = new URLSearchParams(params);
    if (next.mode) {
      if (next.mode === "stock") search.delete("mode");
      else search.set("mode", next.mode);
    }
    if (next.view) {
      if (next.view === "all") search.delete("view");
      else search.set("view", next.view);
    }
    setParams(search);
  };
  const modes: Array<{ key: InventoryMode; label: string; note: string; icon: typeof PackageCheck }> = [
    { key: "stock", label: "재고 현황", note: "수동 재고 조정", icon: PackageCheck },
    { key: "transfer", label: "오프라인 위치 이동", note: "창고/매장 이동", icon: ArrowRightLeft },
    { key: "allocate", label: "온라인몰 할당", note: "채널별 판매 가능 수량", icon: Globe2 }
  ];
  const views: SavedView[] = [
    { key: "all", label: "전체", count: products.length },
    { key: "low", label: "저재고", count: products.filter(productHasLowStock).length },
    { key: "reserved", label: "예약 있음", count: products.filter((product) => productReservedTotal(product) > 0).length },
    { key: "online_mismatch", label: "온라인몰 차이", count: products.filter(productHasOnlineMismatch).length },
    { key: "location_check", label: "위치 확인", count: products.filter(productNeedsLocationCheck).length }
  ];
  const filtered = products.filter((product) => {
    const text = normalizeText([
      product.productName,
      product.productKey,
      product.seasonCode,
      product.categoryName,
      ...product.rows.flatMap((row) => [row.sku, row.readableAlias, row.colorwayName, row.sizeName])
    ].join(" "));
    if (query && !text.includes(normalizeText(query))) return false;
    if (activeView === "low") return productHasLowStock(product);
    if (activeView === "reserved") return productReservedTotal(product) > 0;
    if (activeView === "online_mismatch") return productHasOnlineMismatch(product);
    if (activeView === "location_check") return productNeedsLocationCheck(product);
    return true;
  });
  const mutation = useMutation({
    mutationFn: () => {
      if (!selectedSku) throw new Error("SKU를 선택해 주세요.");
      if (action === "allocate") {
        return api.saveAllocations({
          channelCode: selectedChannelCode,
          locationCode: toLocation,
          allocations: [{ sku: String(selectedSku.sku || ""), quantity: allocationQuantity }]
        });
      }
      if (action === "pushChannelStock") {
        const productKey = selectedSkuProductKey(selectedSku);
        if (!productKey) throw new Error("상품코드를 찾지 못했습니다.");
        return api.pushChannelStock({
          channelCode: selectedChannelCode,
          productKey
        });
      }
      if (action === "transfer") {
        return api.moveInventory({
          sku: String(selectedSku.sku || ""),
          quantity,
          fromLocationCode: fromLocation,
          toLocationCode: toLocation,
          type: "TRANSFER",
          note: reason
        });
      }
      return api.moveInventory({
        sku: String(selectedSku.sku || ""),
        quantity,
        fromLocationCode: adjustmentDirection === "decrease" ? toLocation : undefined,
        toLocationCode: adjustmentDirection === "increase" ? toLocation : undefined,
        type: "ADJUSTMENT",
        note: reason
      });
    },
    onSuccess: async () => {
      setAction(null);
      setReason("");
      await queryClient.invalidateQueries({ queryKey: ["bootstrap"] });
    }
  });
  const readChannelStockMutation = useMutation({
    mutationFn: () => {
      if (!selectedSku) throw new Error("SKU를 선택해 주세요.");
      const productKey = selectedSkuProductKey(selectedSku);
      if (!productKey) throw new Error("상품코드를 찾지 못했습니다.");
      return api.readChannelStock({
        channelCode: selectedChannelCode,
        productKey,
        alignInventory: false
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["bootstrap"] });
    }
  });

  const columns = useMemo<ColumnDef<ProductRow>[]>(() => [
    {
      accessorKey: "productName",
      header: "상품명",
      cell: ({ row }) => (
        <div className="cell-primary">
          <strong>{row.original.productName || "-"}</strong>
        </div>
      ),
      size: 280
    },
    { accessorKey: "productKey", header: "내부 상품코드", cell: ({ row }) => row.original.productKey },
    { accessorKey: "seasonCode", header: "시즌", cell: ({ row }) => row.original.seasonCode },
    { accessorKey: "categoryName", header: "카테고리", cell: ({ row }) => row.original.categoryName },
    { accessorKey: "colorCount", header: "컬러", cell: ({ row }) => <span className="num">{formatNumber(row.original.colorCount)}</span> },
    { accessorKey: "sizeCount", header: "사이즈", cell: ({ row }) => <span className="num">{formatNumber(row.original.sizeCount)}</span> },
    { accessorKey: "skuCount", header: "SKU", cell: ({ row }) => <span className="num">{formatNumber(row.original.skuCount)}</span> },
    { accessorKey: "onHandTotal", header: "실재고", cell: ({ row }) => <span className="num">{formatNumber(row.original.onHandTotal)}</span> },
    { id: "reservedTotal", header: "예약", cell: ({ row }) => <span className="num">{formatNumber(productReservedTotal(row.original))}</span> },
    { accessorKey: "allocatedTotal", header: "온라인 할당", cell: ({ row }) => <span className="num">{formatNumber(row.original.allocatedTotal || 0)}</span> },
    { id: "available", header: "가용", cell: ({ row }) => <span className="num">{formatNumber(productAvailableTotal(row.original))}</span> },
    {
      id: "state",
      header: "상태",
      cell: ({ row }) => productHasOnlineMismatch(row.original)
        ? <StatusBadge tone="warning">온라인 차이</StatusBadge>
        : productHasLowStock(row.original)
          ? <StatusBadge tone="info">저재고</StatusBadge>
          : <StatusBadge tone="success">정상</StatusBadge>
    }
  ], []);

  const selectedSkuText = selectedSku ? `${selectedSku.productName || ""} · ${selectedSku.readableAlias || selectedSku.sku}` : "";
  const actionTitle = action === "transfer"
    ? "오프라인 위치 이동 확인"
    : action === "allocate"
      ? "백오피스 할당 기준 저장 확인"
      : action === "pushChannelStock"
        ? `${selectedChannelLabel(channelOptions, selectedChannelCode)} 재고 반영 승인 요청`
        : "수동 재고 조정 확인";
  const currentValue = selectedSku && action === "transfer"
    ? `${locationLabel(locationOptions, fromLocation)} ${formatNumber(locationQuantity(selectedSku, fromLocation))}개 → ${locationLabel(locationOptions, toLocation)} ${formatNumber(locationQuantity(selectedSku, toLocation))}개`
    : selectedSku && action === "allocate"
      ? `${selectedChannelLabel(channelOptions, selectedChannelCode)} ${formatNumber(Number(selectedSku.allocatedTotal || 0))}개 · ${locationLabel(locationOptions, toLocation)} 할당 가능 ${formatNumber(locationAllocatableQuantity(selectedSku, toLocation, locationOptions))}개`
      : selectedSku && action === "pushChannelStock"
        ? `백오피스 할당 기준 ${formatNumber(Number(selectedSku.allocatedTotal || 0))}개 · 선택 상품 매핑 SKU 전체`
      : selectedSku ? `${locationLabel(locationOptions, toLocation)} ${formatNumber(locationQuantity(selectedSku, toLocation))}개` : "-";
  const nextValue = selectedSku && action === "transfer"
    ? `${locationLabel(locationOptions, fromLocation)} ${formatNumber(locationQuantity(selectedSku, fromLocation) - quantity)}개 → ${locationLabel(locationOptions, toLocation)} ${formatNumber(locationQuantity(selectedSku, toLocation) + quantity)}개`
    : selectedSku && action === "allocate"
      ? `${selectedChannelLabel(channelOptions, selectedChannelCode)} ${formatNumber(allocationQuantity)}개 · ${locationLabel(locationOptions, toLocation)} 기준`
      : selectedSku && action === "pushChannelStock"
        ? `${selectedChannelLabel(channelOptions, selectedChannelCode)} 재고 반영 승인 요청 생성`
      : selectedSku ? `${locationLabel(locationOptions, toLocation)} ${formatNumber(adjustedLocationQuantity(selectedSku, toLocation, quantity, adjustmentDirection))}개` : "-";

  useEffect(() => {
    if (activeMode !== "allocate" || !selectedSku) return;
    const nextLocation = preferredAllocationLocation(locationOptions, selectedSku, toLocation);
    if (nextLocation && nextLocation !== toLocation) setToLocation(nextLocation);
  }, [activeMode, selectedSku, toLocation, locationOptions]);

  useEffect(() => {
    if (activeMode !== "transfer" || fromLocation !== toLocation || locationOptions.length <= 1) return;
    const nextLocation = locationOptions.find((location) => String(location.code) !== String(fromLocation));
    if (nextLocation) setToLocation(String(nextLocation.code));
  }, [activeMode, fromLocation, toLocation, locationOptions]);

  const table = (
    <DataTable
      columns={columns}
      data={filtered}
      getRowId={(row) => row.productKey}
      selectedId={expandedProductKey || ""}
      onSelect={(record) => {
        setExpandedProductKey((current) => current === record.productKey ? null : record.productKey);
      }}
      renderExpandedRow={(record) => (
        <InventorySkuPreview
          product={record}
          mode={activeMode}
          selectedSku={selectedSku}
          onSelectSku={(sku) => {
            setSelectedSku(sku);
            setAllocationQuantity(Number(sku.allocatedTotal || 0));
            if (activeMode === "allocate") {
              const nextLocation = preferredAllocationLocation(locationOptions, sku, toLocation);
              if (nextLocation) setToLocation(nextLocation);
            }
            setDrawer(null);
          }}
        />
      )}
      emptyTitle="표시할 재고가 없습니다."
    />
  );

  return (
    <div className="page-stack">
      <section className="toolbar-panel">
        <div className="inventory-mode-tabs" role="tablist" aria-label="Inventory 작업">
          {modes.map((mode) => {
            const Icon = mode.icon;
            return (
              <button
                key={mode.key}
                type="button"
                role="tab"
                aria-selected={activeMode === mode.key}
                className={activeMode === mode.key ? "inventory-mode-tab is-active" : "inventory-mode-tab"}
                onClick={() => {
                  setAction(null);
                  if (mode.key === "transfer" && fromLocation === toLocation && locationOptions.length > 1) {
                    const nextLocation = locationOptions.find((location) => String(location.code) !== String(fromLocation));
                    if (nextLocation) setToLocation(String(nextLocation.code));
                  }
                  if (mode.key === "allocate" && selectedSku) {
                    const nextLocation = preferredAllocationLocation(locationOptions, selectedSku, toLocation);
                    if (nextLocation) setToLocation(nextLocation);
                  }
                  setInventoryParams({ mode: mode.key });
                }}
              >
                <Icon size={15} />
                <span>{mode.label}</span>
                <small>{mode.note}</small>
              </button>
            );
          })}
        </div>
        <SavedViews views={views} active={activeView} onChange={(view) => setInventoryParams({ view })} />
        <div className="toolbar-row">
          <label className="search-field">
            검색
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="상품명, 상품코드, SKU, 컬러, 사이즈" />
          </label>
        </div>
      </section>
      {selectedSku ? (
        <section className="inventory-workspace-grid">
          <div className="panel inventory-table-pane">{table}</div>
          <InventoryOperationPanel
            activeMode={activeMode}
            selectedSku={selectedSku}
            selectedSkuText={selectedSkuText}
            locationOptions={locationOptions}
            channelOptions={channelOptions}
            adjustmentDirection={adjustmentDirection}
            quantity={quantity}
            allocationQuantity={allocationQuantity}
            fromLocation={fromLocation}
            toLocation={toLocation}
            selectedChannelCode={selectedChannelCode}
            onAdjustmentDirectionChange={setAdjustmentDirection}
            onQuantityChange={setQuantity}
            onAllocationQuantityChange={setAllocationQuantity}
            onFromLocationChange={setFromLocation}
            onToLocationChange={setToLocation}
            onChannelChange={setSelectedChannelCode}
            onAction={setAction}
            onReadChannelStock={() => readChannelStockMutation.mutate()}
            readChannelStockBusy={readChannelStockMutation.isPending}
            pushChannelStockBusy={mutation.isPending && action === "pushChannelStock"}
          />
        </section>
      ) : (
        <section className="panel">{table}</section>
      )}
      <ConfirmImpactDialog
        open={Boolean(action && selectedSku)}
        title={actionTitle}
        currentValue={currentValue}
        nextValue={nextValue}
        externalImpact={action === "pushChannelStock"
          ? "확인 즉시 Cafe24에 쓰지 않습니다. 승인 요청과 outbox를 만들고, 승인 후 외부몰에 적용합니다."
          : action === "allocate"
            ? "Cafe24 실제 재고는 지금 변경하지 않습니다. 백오피스 할당 기준만 저장하고, 온라인몰 반영은 별도 재고 동기화 작업에서 실행합니다."
            : "Cafe24나 다른 온라인몰에는 즉시 쓰지 않습니다. 온라인몰 할당은 별도 작업으로 저장합니다."}
        auditTrail={action === "pushChannelStock"
          ? "상품, 온라인몰 매핑 SKU, 요청 수량, 사유가 승인 요청과 감사 로그에 남습니다."
          : action === "allocate"
            ? "SKU, 온라인몰, 기준 위치, 할당 기준 수량, 사유가 감사 로그에 남습니다."
            : "SKU, 위치, 수량, 사유가 재고 원장과 감사 로그에 남습니다."}
        reason={reason}
        confirmLabel={action === "transfer" ? "위치 이동" : action === "allocate" ? "할당 기준 저장" : action === "pushChannelStock" ? "승인 요청 생성" : "수동 재고 조정"}
        busy={mutation.isPending}
        onReasonChange={setReason}
        onCancel={() => setAction(null)}
        onConfirm={() => mutation.mutate()}
      />
    </div>
  );
}

function InventoryOperationPanel({
  activeMode,
  selectedSku,
  selectedSkuText,
  locationOptions,
  channelOptions,
  adjustmentDirection,
  quantity,
  allocationQuantity,
  fromLocation,
  toLocation,
  selectedChannelCode,
  onAdjustmentDirectionChange,
  onQuantityChange,
  onAllocationQuantityChange,
  onFromLocationChange,
  onToLocationChange,
  onChannelChange,
  onAction,
  onReadChannelStock,
  readChannelStockBusy,
  pushChannelStockBusy
}: {
  activeMode: InventoryMode;
  selectedSku: SkuRow;
  selectedSkuText: string;
  locationOptions: AnyRecord[];
  channelOptions: Array<{ code: string; name: string }>;
  adjustmentDirection: AdjustmentDirection;
  quantity: number;
  allocationQuantity: number;
  fromLocation: string;
  toLocation: string;
  selectedChannelCode: string;
  onAdjustmentDirectionChange: (direction: AdjustmentDirection) => void;
  onQuantityChange: (quantity: number) => void;
  onAllocationQuantityChange: (quantity: number) => void;
  onFromLocationChange: (location: string) => void;
  onToLocationChange: (location: string) => void;
  onChannelChange: (channelCode: string) => void;
  onAction: (action: PendingAction) => void;
  onReadChannelStock: () => void;
  readChannelStockBusy: boolean;
  pushChannelStockBusy: boolean;
}) {
  const stockLimit = Number(selectedSku.onHandTotal || 0) - Number(selectedSku.reservedTotal || 0);
  const sourceQuantity = locationQuantity(selectedSku, fromLocation);
  const targetQuantity = locationQuantity(selectedSku, toLocation);
  const adjustedTargetQuantity = adjustedLocationQuantity(selectedSku, toLocation, quantity, adjustmentDirection);
  const allocationLocationOptions = sellableLocationOptions(locationOptions);
  const allocationAvailable = locationAllocatableQuantity(selectedSku, toLocation, locationOptions);
  const allocationLocationIsSellable = isSellableLocationCode(locationOptions, toLocation);
  const transferInvalid = fromLocation === toLocation || quantity > sourceQuantity;
  const adjustmentInvalid = adjustmentDirection === "decrease" && quantity > targetQuantity;
  const allocationInvalid = !allocationLocationIsSellable || allocationQuantity > allocationAvailable;
  const allocationWarning = allocationQuantity > Math.max(0, stockLimit);
  const channelSyncInvalid = !selectedSkuProductKey(selectedSku);
  const actionLabel = activeMode === "transfer" ? "위치 이동" : activeMode === "allocate" ? "할당 기준 저장" : "수동 재고 조정";
  const actionIcon = activeMode === "transfer" ? <ArrowRightLeft size={15} /> : activeMode === "allocate" ? <Globe2 size={15} /> : <Plus size={15} />;
  const guide = activeMode === "transfer"
    ? "물리 재고를 위치 간 이동합니다. 출발/도착 위치와 이동 후 수량을 확인하세요."
    : activeMode === "allocate"
      ? "Cafe24 실제 재고는 바꾸지 않고 백오피스의 온라인 판매 기준 수량만 저장합니다."
      : "실사, 파손, 샘플 출고처럼 원장 보정이 필요한 경우만 사용합니다.";

  return (
    <aside className="inventory-operation-panel" aria-label="선택 SKU 작업 흐름">
      <div className="inventory-operation-head">
        <span>{activeMode === "transfer" ? "오프라인 위치 이동" : activeMode === "allocate" ? "온라인몰 할당 기준" : "재고 현황"}</span>
        <h2>{selectedSku.readableAlias || selectedSku.sku}</h2>
        <p>{selectedSkuText}</p>
      </div>

      <section className="inventory-operation-section">
        <h3>현재 재고</h3>
        <div className="inventory-metric-grid">
          <Metric label="실재고" value={selectedSku.onHandTotal ?? 0} />
          <Metric label="예약" value={selectedSku.reservedTotal ?? 0} />
          <Metric label="온라인 할당" value={selectedSku.allocatedTotal ?? 0} />
          <Metric label="가용" value={selectedSku.availableToSell ?? 0} />
        </div>
      </section>

      <section className="inventory-operation-section">
        <h3>작업</h3>
        <p className="muted">{guide}</p>
        {activeMode === "stock" ? (
          <div className="inventory-operation-form">
            <div className="segmented-control" role="group" aria-label="조정 방식">
              <button type="button" className={adjustmentDirection === "increase" ? "is-active" : ""} onClick={() => onAdjustmentDirectionChange("increase")}>수량 추가</button>
              <button type="button" className={adjustmentDirection === "decrease" ? "is-active" : ""} onClick={() => onAdjustmentDirectionChange("decrease")}>수량 차감</button>
            </div>
            <label>
              조정 위치
              <select value={toLocation} onChange={(event) => onToLocationChange(event.target.value)}>
                {locationOptions.map((location) => <option key={String(location.code)} value={String(location.code)}>{location.name || location.code}</option>)}
              </select>
            </label>
            <label>
              조정 수량
              <input type="number" min={1} value={quantity} onChange={(event) => onQuantityChange(Math.max(1, Number(event.target.value || 1)))} />
            </label>
            {adjustmentInvalid ? <p className="form-error">차감 수량이 현재 위치 수량보다 큽니다.</p> : null}
          </div>
        ) : null}
        {activeMode === "transfer" ? (
          <div className="inventory-operation-form">
            <label>
              출발 위치
              <select value={fromLocation} onChange={(event) => onFromLocationChange(event.target.value)}>
                {locationOptions.map((location) => <option key={String(location.code)} value={String(location.code)}>{location.name || location.code}</option>)}
              </select>
            </label>
            <label>
              도착 위치
              <select value={toLocation} onChange={(event) => onToLocationChange(event.target.value)}>
                {locationOptions.map((location) => <option key={String(location.code)} value={String(location.code)}>{location.name || location.code}</option>)}
              </select>
            </label>
            <label>
              이동 수량
              <input type="number" min={1} value={quantity} onChange={(event) => onQuantityChange(Math.max(1, Number(event.target.value || 1)))} />
            </label>
            {fromLocation === toLocation ? <p className="form-error">출발 위치와 도착 위치가 같습니다.</p> : null}
            {quantity > sourceQuantity ? <p className="form-error">이동 수량이 출발 위치 수량보다 큽니다.</p> : null}
          </div>
        ) : null}
        {activeMode === "allocate" ? (
          <div className="inventory-operation-form">
            <label>
              할당 기준 위치
              <select value={toLocation} onChange={(event) => onToLocationChange(event.target.value)}>
                {allocationLocationOptions.map((location) => <option key={String(location.code)} value={String(location.code)}>{location.name || location.code}</option>)}
              </select>
            </label>
            <label>
              온라인몰
              <select value={selectedChannelCode} onChange={(event) => onChannelChange(event.target.value)}>
                {channelOptions.map((channel) => <option key={channel.code} value={channel.code}>{channel.name}</option>)}
              </select>
            </label>
            <label>
              할당 수량
              <input type="number" min={0} value={allocationQuantity} onChange={(event) => onAllocationQuantityChange(Math.max(0, Number(event.target.value || 0)))} />
            </label>
            <p className="muted">선택 위치 할당 가능 {formatNumber(allocationAvailable)}개 · 비판매 위치 재고는 온라인 할당 기준에서 제외됩니다.</p>
            {!allocationLocationOptions.length ? <p className="form-error">판매 가능한 재고 위치가 없습니다.</p> : null}
            {allocationLocationOptions.length && !allocationLocationIsSellable ? <p className="form-error">판매 가능한 위치를 선택해 주세요.</p> : null}
            {allocationQuantity > allocationAvailable ? <p className="form-error">선택 위치의 할당 가능 수량은 {formatNumber(allocationAvailable)}개입니다.</p> : null}
            {allocationWarning && allocationQuantity <= allocationAvailable ? <p className="form-error">할당 수량이 예약 제외 실재고보다 큽니다.</p> : null}
          </div>
        ) : null}
      </section>

      <section className="inventory-operation-section">
        <h3>변경 미리보기</h3>
        {activeMode === "stock" ? (
          <ImpactRows
            rows={[
              { label: "위치", before: locationLabel(locationOptions, toLocation), after: locationLabel(locationOptions, toLocation) },
              { label: "수량", before: `${formatNumber(targetQuantity)}개`, after: `${formatNumber(adjustedTargetQuantity)}개` }
            ]}
          />
        ) : null}
        {activeMode === "transfer" ? (
          <ImpactRows
            rows={[
              { label: "출발", before: `${locationLabel(locationOptions, fromLocation)} ${formatNumber(sourceQuantity)}개`, after: `${locationLabel(locationOptions, fromLocation)} ${formatNumber(sourceQuantity - quantity)}개` },
              { label: "도착", before: `${locationLabel(locationOptions, toLocation)} ${formatNumber(targetQuantity)}개`, after: `${locationLabel(locationOptions, toLocation)} ${formatNumber(targetQuantity + quantity)}개` }
            ]}
          />
        ) : null}
        {activeMode === "allocate" ? (
          <PreviewRows
            rows={[
              { label: "기준 위치 할당 가능", value: `${locationLabel(locationOptions, toLocation)} · ${formatNumber(allocationAvailable)}개` },
              { label: "현재 온라인몰 할당", value: `${formatNumber(Number(selectedSku.allocatedTotal || 0))}개` },
              { label: "변경할 할당 수량", value: `${formatNumber(allocationQuantity)}개` }
            ]}
          />
        ) : null}
        <p className="inventory-impact-note">확인 단계에서 사유를 입력하면 재고 원장과 감사 로그에 기록됩니다.</p>
      </section>

      <section className="inventory-operation-section">
        <h3>{activeMode === "allocate" ? "할당 가능 위치" : "위치별 수량"}</h3>
        <div className="inventory-location-list">
          {activeMode === "allocate" ? (
            allocationLocationOptions.map((location) => {
              const code = String(location.code);
              const quantityOnLocation = locationQuantity(selectedSku, code);
              const available = locationAllocatableQuantity(selectedSku, code, locationOptions);
              return (
                <div key={code} className={code === toLocation ? "is-selected" : ""}>
                  <span>{locationLabel(locationOptions, code)}</span>
                  <strong>실재고 {formatNumber(quantityOnLocation)} · 할당 가능 {formatNumber(available)}</strong>
                </div>
              );
            })
          ) : (
            Object.entries(selectedSku.balances || {}).map(([location, value]) => (
              <div key={location}>
                <span>{locationLabel(locationOptions, location)}</span>
                <strong>{formatNumber(Number(value || 0))}</strong>
              </div>
            ))
          )}
        </div>
      </section>

      <button
        type="button"
        className="primary inventory-operation-submit"
        disabled={activeMode === "transfer" ? transferInvalid : activeMode === "stock" ? adjustmentInvalid : allocationInvalid}
        onClick={() => onAction(activeMode === "transfer" ? "transfer" : activeMode === "allocate" ? "allocate" : "adjust")}
      >
        {actionIcon}
        {actionLabel}
      </button>

      {activeMode === "allocate" ? (
        <section className="inventory-operation-section inventory-channel-sync-section">
          <h3>온라인몰 재고 동기화</h3>
          <p className="muted">
            할당 기준 저장 후 Cafe24 현재고를 대조하거나, 선택 상품의 매핑 SKU 전체에 대한 외부몰 반영 승인 요청을 만듭니다.
          </p>
          <div className="inventory-channel-sync-actions">
            <button type="button" onClick={onReadChannelStock} disabled={readChannelStockBusy || channelSyncInvalid}>
              <RefreshCw size={15} />
              {readChannelStockBusy ? "현재고 읽는 중" : "Cafe24 현재고 읽기"}
            </button>
            <button type="button" className="danger" onClick={() => onAction("pushChannelStock")} disabled={pushChannelStockBusy || channelSyncInvalid}>
              <UploadCloud size={15} />
              {pushChannelStockBusy ? "요청 중" : "Cafe24 반영 요청"}
            </button>
          </div>
        </section>
      ) : null}
    </aside>
  );
}

function Metric({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="inventory-metric">
      <span>{label}</span>
      <strong>{typeof value === "number" ? formatNumber(value) : value}</strong>
    </div>
  );
}

function ImpactRows({ rows }: { rows: Array<{ label: string; before: string; after: string }> }) {
  return (
    <div className="inventory-impact-list">
      {rows.map((row) => (
        <div key={row.label}>
          <span>{row.label}</span>
          <strong>{row.before}</strong>
          <b>{row.after}</b>
        </div>
      ))}
    </div>
  );
}

function PreviewRows({ rows }: { rows: Array<{ label: string; value: string }> }) {
  return (
    <div className="inventory-preview-list">
      {rows.map((row) => (
        <div key={row.label}>
          <span>{row.label}</span>
          <strong>{row.value}</strong>
        </div>
      ))}
    </div>
  );
}

function InventorySkuPreview({ product, mode, selectedSku, onSelectSku }: { product: ProductRow; mode: InventoryMode; selectedSku: SkuRow | null; onSelectSku: (sku: SkuRow) => void }) {
  const rows = sortedSkuRows(product.rows);
  const guide = mode === "transfer" ? "SKU 선택 후 위치 이동" : mode === "allocate" ? "SKU 선택 후 할당 기준 저장" : "SKU 선택 후 수동 재고 조정";
  return (
    <div className="products-sku-preview inventory-sku-preview">
      <div className="products-sku-preview-head">
        <h3>SKU 재고 매트릭스</h3>
        <span>{formatNumber(rows.length)} SKU · {guide}</span>
      </div>
      <div className="table-frame">
        <table className="data-table product-sku-table">
          <thead>
            <tr>
              <th>SKU</th>
              <th>Readable alias</th>
              <th>바코드</th>
              <th>컬러</th>
              <th>사이즈</th>
              <th>실재고</th>
              <th>예약</th>
              <th>온라인 할당</th>
              <th>가용</th>
              <th>상태</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const isSelected = String(selectedSku?.sku || "") === String(row.sku || "");
              return (
                <tr key={String(row.sku || row.readableAlias)} className={isSelected ? "is-selected" : ""} onClick={() => onSelectSku(row)}>
                  <td><strong>{row.sku || "-"}</strong></td>
                  <td>{row.readableAlias || "-"}</td>
                  <td>{barcodeValue(row) || "미발급"}</td>
                  <td>{row.colorwayName || row.colorwayCode || "-"}</td>
                  <td>{formatSizeLabel(row.sizeName || row.sizeCode)}</td>
                  <td><span className="num">{formatNumber(Number(row.onHandTotal || 0))}</span></td>
                  <td><span className="num">{formatNumber(Number(row.reservedTotal || 0))}</span></td>
                  <td><span className="num">{formatNumber(Number(row.allocatedTotal || 0))}</span></td>
                  <td><span className="num">{formatNumber(Number(row.availableToSell || 0))}</span></td>
                  <td>{hasOnlineMismatch(row) ? <StatusBadge tone="warning">온라인 차이</StatusBadge> : Number(row.availableToSell || 0) <= 3 ? <StatusBadge tone="info">저재고</StatusBadge> : <StatusBadge tone="success">정상</StatusBadge>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function sortedSkuRows(rows: SkuRow[]): SkuRow[] {
  return [...rows].sort((a, b) =>
    String(a.colorwayName || a.colorwayCode || "").localeCompare(String(b.colorwayName || b.colorwayCode || ""), "ko") ||
    String(a.sizeName || a.sizeCode || "").localeCompare(String(b.sizeName || b.sizeCode || ""), "ko")
  );
}

function barcodeValue(row: SkuRow): string {
  return String(row.barcode || row.gtin || row.ean13 || row.ean || "").trim();
}

function selectedSkuProductKey(row: SkuRow): string {
  return String(row.productKey || row.productCode || row.customProductCode || "").trim();
}

function productReservedTotal(product: ProductRow): number {
  return product.rows.reduce((sum, row) => sum + Number(row.reservedTotal || 0), 0);
}

function productAvailableTotal(product: ProductRow): number {
  return product.rows.reduce((sum, row) => sum + Number(row.availableToSell || 0), 0);
}

function productHasLowStock(product: ProductRow): boolean {
  return product.rows.some((row) => Number(row.availableToSell || 0) <= 3);
}

function productHasOnlineMismatch(product: ProductRow): boolean {
  return product.rows.some(hasOnlineMismatch);
}

function productNeedsLocationCheck(product: ProductRow): boolean {
  return product.rows.some((row) => Number(row.onHandTotal || 0) < 0 || Number(row.transferableTotal || 0) < 0);
}

function hasOnlineMismatch(row: SkuRow): boolean {
  const listings = Array.isArray(row.channelListings) ? row.channelListings : [];
  return listings.some((listing) => {
    if (listing.remoteQuantity === null || listing.remoteQuantity === undefined) return false;
    return Number(listing.remoteQuantity || 0) !== Number(listing.allocatedQuantity || 0);
  });
}

function locationQuantity(row: SkuRow, locationCode: string): number {
  return Number(row.balances?.[locationCode] || 0);
}

function isSellableLocation(location: AnyRecord | undefined): boolean {
  return Boolean(location && (Number(location.isSellable ?? 0) === 1 || location.sellableState === "sellable"));
}

function sellableLocationOptions(locations: AnyRecord[]): AnyRecord[] {
  return locations.filter(isSellableLocation);
}

function isSellableLocationCode(locations: AnyRecord[], code: string): boolean {
  return isSellableLocation(locations.find((location) => String(location.code) === String(code)));
}

function locationAllocatableQuantity(row: SkuRow, locationCode: string, locations: AnyRecord[]): number {
  if (!isSellableLocationCode(locations, locationCode)) return 0;
  const quantity = locationQuantity(row, locationCode);
  const reserved = Number(row.reservedTotal || 0);
  return Math.max(0, quantity - reserved);
}

function preferredAllocationLocation(locations: AnyRecord[], row: SkuRow, currentCode: string): string {
  const sellable = sellableLocationOptions(locations);
  if (!sellable.length) return currentCode || "";
  const current = sellable.find((location) => String(location.code) === String(currentCode));
  if (current && locationQuantity(row, currentCode) > 0) return String(current.code);
  const stocked = sellable.find((location) => locationQuantity(row, String(location.code)) > 0);
  return String(stocked?.code || current?.code || sellable[0]?.code || currentCode || "");
}

function adjustedLocationQuantity(row: SkuRow, locationCode: string, quantity: number, direction: AdjustmentDirection): number {
  const current = locationQuantity(row, locationCode);
  return direction === "decrease" ? current - quantity : current + quantity;
}

function locationLabel(locations: AnyRecord[], code: string): string {
  const location = locations.find((item) => String(item.code) === String(code));
  return String(location?.name || code || "위치");
}

function onlineMallChannels(data: AppData): Array<{ code: string; name: string }> {
  const channels = Array.isArray(data.inventory?.channels) ? data.inventory.channels as AnyRecord[] : [];
  const providerOptions = Array.isArray((data.onlineMallApis as AnyRecord | undefined)?.providers)
    ? (data.onlineMallApis as AnyRecord).providers as AnyRecord[]
    : [];
  const connectionOptions = Array.isArray((data.onlineMallApis as AnyRecord | undefined)?.connections)
    ? (data.onlineMallApis as AnyRecord).connections as AnyRecord[]
    : [];
  const usableProviders = new Set<string>();
  const usableChannelCodes = new Set<string>();

  for (const provider of [...providerOptions, ...connectionOptions]) {
    const providerCode = String(provider.provider || "").toLowerCase();
    const channelCode = String(provider.channelCode || "").toUpperCase();
    const connected = provider.connected === true || ["connected", "configured", "active"].includes(String(provider.status || "").toLowerCase());
    const implemented = String(provider.implementationStatus || "").toLowerCase() === "implemented";
    if (!connected || !implemented) continue;
    if (providerCode) usableProviders.add(providerCode);
    if (channelCode) usableChannelCodes.add(channelCode);
  }

  if (data.status?.cafe24Connected) {
    usableProviders.add("cafe24");
    usableChannelCodes.add("CAFE24_KR");
  }

  const options = channels
    .filter((channel) => {
      const kind = String(channel.kind || channel.channelKind || "").toLowerCase();
      const provider = String(channel.provider || "").toLowerCase();
      const code = String(channel.code || channel.channelCode || "").toUpperCase();
      const status = String(channel.status || "").toLowerCase();
      if (kind === "offline_pos" || provider === "pos" || code.includes("POS")) return false;
      if (["planned", "draft", "disabled", "inactive"].includes(status)) return false;
      return usableChannelCodes.has(code) || usableProviders.has(provider);
    })
    .map((channel) => ({
      code: String(channel.code || channel.channelCode || "").trim(),
      name: String(channel.name || channel.channelName || channel.code || "온라인몰").trim()
    }))
    .filter((channel) => channel.code);
  return uniqueChannels(options.length ? options : data.status?.cafe24Connected ? [{ code: "CAFE24_KR", name: "Cafe24" }] : []);
}

function uniqueChannels(channels: Array<{ code: string; name: string }>): Array<{ code: string; name: string }> {
  const seen = new Set<string>();
  return channels.filter((channel) => {
    const code = channel.code.toUpperCase();
    if (!code || seen.has(code)) return false;
    seen.add(code);
    return true;
  });
}

function selectedChannelLabel(channels: Array<{ code: string; name: string }>, code: string): string {
  return channels.find((channel) => channel.code === code)?.name || code || "온라인몰";
}
